/**
 * Каталог в «Чатах» — фильтр «Резерва» (`a.unirenter.ru … page=productSearch`) один в один: слева
 * группы аккордеоном в том же порядке (открытые и свёрнутые — как там, спрятанные значения — под «Все»),
 * справа результаты. Как в Резерве, выбор в фильтре только пересчитывает «Показать N» — выдача
 * появляется по кнопке. Сверх Резерва: отмеченные фото (до 10) «Фото в ответ» кладёт в поле ответа,
 * отправляет сотрудник сам. Кнопка «Похожие» на плитке — похожие этой модели по фото (тот же блок,
 * что в карточке серии) в городе фильтра. Спека `docs/specs/2026-10-02-dress-catalog-design.md`.
 */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, Check, ChevronDown, ChevronUp, Images, Search, X } from "lucide-react";
import { useEffect, useState } from "react";

import { MAX_PICK, photoFile } from "@/components/chat/DressSearch";
import { SerialModels } from "@/components/chat/SerialModels";
import {
  type CatalogCard,
  type CatalogChoice,
  type CatalogGroup,
  catalogQuery,
  dressCatalog,
  dressCatalogPick,
  markDress,
  dressFilters,
} from "@/lib/clientChats";

const CITY_KEY = "prokat.dress.city";
const PAGE = 48;
const BLUE = "#007bff"; // цвет активной кнопки и «Показать» в Резерве
const savedCity = (): CatalogChoice => {
  const v = Number(localStorage.getItem(CITY_KEY));
  return Number.isInteger(v) && v > 0 ? { locationID: [String(v)] } : {};
};

export function DressCatalog({
  chatId,
  onClose,
  onOffer,
}: {
  chatId: number;
  onClose: () => void;
  onOffer: (text: string, files: File[]) => void;
}) {
  // choice — что нажато в фильтре; applied — по чему показана выдача (null — «Показать» ещё не жали).
  const [choice, setChoice] = useState<CatalogChoice>(savedCity);
  const [applied, setApplied] = useState<CatalogChoice | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [all, setAll] = useState<string | null>(null);
  const [view, setView] = useState<"filters" | "results">("filters");
  // Отмеченное живёт дольше фильтра: можно взять два свадебных, сменить фильтр и добавить вечернее.
  const [picked, setPicked] = useState<CatalogCard[]>([]);
  // Плитка, у которой открыли «Похожие»: блок поверх выдачи, фильтр и отмеченное не трогает.
  const [like, setLike] = useState<CatalogCard | null>(null);

  // Свойства зависят от категории (не выбрана — платья), как в Резерве.
  const category = choice.cat ?? [];
  const filters = useQuery({
    queryKey: ["dress-filters", category],
    queryFn: () => dressFilters(category),
    staleTime: 10 * 60_000,
    placeholderData: keepPreviousData,
  });
  const groups = filters.data?.groups ?? [];
  // Сменилась категория — групп прежней нет: их выбор снимаем.
  useEffect(() => {
    if (!filters.data || filters.isPlaceholderData) return;
    const keys = new Set(filters.data.groups.map((g) => g.key));
    setChoice((had) => (Object.keys(had).every((k) => keys.has(k)) ? had : Object.fromEntries(Object.entries(had).filter(([k]) => keys.has(k)))));
  }, [filters.data, filters.isPlaceholderData]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && (like ? setLike(null) : all ? setAll(null) : onClose());
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, all, like]);

  const draft = catalogQuery(groups, choice).toString();
  const count = useQuery({
    queryKey: ["dress-catalog-count", draft],
    queryFn: () => dressCatalog(new URLSearchParams(draft), 0, 0),
    enabled: groups.length > 0,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
  const shown = applied ? catalogQuery(groups, applied).toString() : null;
  const q = useInfiniteQuery({
    queryKey: ["dress-catalog", shown],
    queryFn: ({ pageParam }) => dressCatalog(new URLSearchParams(shown ?? ""), pageParam, PAGE),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.cards.length === PAGE ? last.offset + PAGE : undefined),
    enabled: shown !== null,
    staleTime: 60_000,
    retry: false,
  });
  const cards = q.data?.pages.flatMap((p) => p.cards) ?? [];

  const isOpen = (g: CatalogGroup) => open[g.key] ?? (!g.collapsed || (choice[g.key]?.length ?? 0) > 0);
  const flip = (g: CatalogGroup, id: string) =>
    setChoice((had) => {
      const was = had[g.key] ?? [];
      const next = was.includes(id) ? was.filter((x) => x !== id) : g.multiple ? [...was, id] : [id];
      return { ...had, [g.key]: next };
    });
  const show = (next: CatalogChoice = choice) => {
    const city = next.locationID?.[0];
    if (city) localStorage.setItem(CITY_KEY, city);
    else localStorage.removeItem(CITY_KEY);
    setApplied(next);
    setView("results");
  };
  // Крестик на активном фильтре над выдачей: снять и сразу показать заново — как в Резерве.
  const drop = (g: CatalogGroup, id: string) => {
    const next = { ...choice, [g.key]: (choice[g.key] ?? []).filter((x) => x !== id) };
    setChoice(next);
    show(next);
  };
  const ids = picked.map((c) => c.model_id);
  const toggle = (card: CatalogCard) =>
    setPicked((had) =>
      had.some((c) => c.model_id === card.model_id)
        ? had.filter((c) => c.model_id !== card.model_id)
        : had.length < MAX_PICK
          ? [...had, card]
          : had,
    );
  const offer = useMutation({
    mutationFn: async () => {
      const files = await Promise.all(picked.map(photoFile));
      files.forEach((f, i) => markDress(f, { origin: "catalog", model_id: picked[i]!.model_id }));
      await dressCatalogPick(chatId, ids, new URLSearchParams(shown ?? ""));
      return files;
    },
    onSuccess: (files) => {
      onOffer("", files);
      onClose();
    },
  });
  const allGroup = groups.find((g) => g.key === all);
  // Город выдачи — салон похожих сразу; без города — «Все города».
  const city = Number(applied?.locationID?.[0]) || 0;

  return (
    <div className="fixed inset-0 z-50 flex bg-black/60 sm:p-3" onClick={onClose}>
      <div
        className="flex min-w-0 flex-1 overflow-hidden bg-white text-[14px] text-[#212529] sm:rounded-[12px]"
        onClick={(e) => e.stopPropagation()}
        data-annot="каталог платьев"
      >
        {/* Слева — фильтры */}
        <div
          className={`relative w-full flex-none flex-col border-r border-[#d9d9d9] md:flex md:w-[340px] lg:w-[400px] ${view === "filters" ? "flex" : "hidden"}`}
          data-annot="фильтр каталога"
        >
          <div className="flex h-10 flex-none items-center border-b border-[#d9d9d9] bg-[#f2f2f7] px-3 text-[16px]">
            <button type="button" onClick={onClose} title="Закрыть" className="rounded p-1 text-[#6c757d] hover:bg-black/5 md:hidden">
              <X size={18} />
            </button>
            <span className="flex-1 text-center">Фильтры</span>
            <button type="button" onClick={() => setChoice({})} className="hover:text-[#007bff]">
              Сбросить
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {filters.isLoading && <div className="p-4 text-[#6c757d]">Загружаем фильтры…</div>}
            {filters.isError && <div className="p-4 text-[#e53935]">{(filters.error as Error).message}</div>}
            {groups.map((g) => (
              <div key={g.key} className="border-b border-[#d9d9d9]">
                <div
                  className="flex cursor-pointer items-center gap-3 px-2 py-2.5"
                  onClick={() => setOpen({ ...open, [g.key]: !isOpen(g) })}
                >
                  <span className="flex-1 font-semibold">{g.name}</span>
                  {g.values.some((v) => v.hide) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAll(g.key);
                      }}
                      className="text-[#007bff] hover:underline"
                    >
                      Все
                    </button>
                  )}
                  {isOpen(g) ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
                {isOpen(g) && (
                  <div className="flex flex-wrap gap-x-1.5 gap-y-1.5 px-2 pb-4">
                    {g.template === "text" ? (
                      <input
                        value={choice[g.key]?.[0] ?? ""}
                        onChange={(e) => setChoice({ ...choice, [g.key]: e.target.value ? [e.target.value] : [] })}
                        onKeyDown={(e) => e.key === "Enter" && show()}
                        className="h-8 w-full rounded border border-[#ced4da] px-2 outline-none focus:border-[#007bff]"
                      />
                    ) : (
                      g.values
                        // Спрятанное под «Все» появляется среди кнопок, только когда выбрано.
                        .filter((v) => !v.hide || choice[g.key]?.includes(v.id))
                        .map((v) => {
                          const active = choice[g.key]?.includes(v.id);
                          return (
                            <button
                              key={v.id}
                              type="button"
                              onClick={() => flip(g, v.id)}
                              className="flex h-[26px] items-center gap-1.5 rounded-full px-2.5 leading-none"
                              style={active ? { background: BLUE, color: "#fff" } : { background: "#efeff0" }}
                            >
                              {v.color && (
                                <span className="size-3.5 flex-none rounded-full border border-black/20" style={{ background: v.color }} />
                              )}
                              {v.name}
                            </button>
                          );
                        })
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex-none border-t border-[#d9d9d9] bg-[#f2f2f7] p-2.5">
            <button
              type="button"
              onClick={() => show()}
              className="h-11 w-full rounded-[6px] text-[16px] text-white"
              style={{ background: BLUE }}
              data-annot="показать"
            >
              Показать {count.data?.total ?? ""}
            </button>
          </div>
          {allGroup && <AllValues group={allGroup} chosen={choice[allGroup.key] ?? []} onFlip={(id) => flip(allGroup, id)} onBack={() => setAll(null)} />}
        </div>

        {/* Справа — результаты */}
        <div className={`relative min-w-0 flex-1 flex-col md:flex ${view === "results" ? "flex" : "hidden"}`}>
          <div className="flex h-10 flex-none items-center border-b border-[#d9d9d9] bg-[#f2f2f7] px-3 text-[16px]">
            <button type="button" onClick={() => setView("filters")} className="text-[#007bff] md:hidden">
              Фильтры
            </button>
            <span className="flex-1 text-center">Результаты</span>
            <button type="button" onClick={onClose} title="Закрыть" className="rounded p-1 text-[#6c757d] hover:bg-black/5">
              <X size={18} />
            </button>
          </div>
          <div
            className="min-h-0 flex-1 overflow-y-auto bg-[#f2f2f7] p-1.5"
            onScroll={(e) => {
              const el = e.currentTarget;
              if (q.hasNextPage && !q.isFetchingNextPage && el.scrollHeight - el.clientHeight - el.scrollTop <= el.clientHeight * 2)
                void q.fetchNextPage();
            }}
          >
            {applied && (
              <div className="flex flex-wrap gap-1.5 px-1 pb-2 pt-1">
                {groups.flatMap((g) =>
                  (applied[g.key] ?? []).map((id) => (
                    <button
                      key={`${g.key}:${id}`}
                      type="button"
                      onClick={() => drop(g, id)}
                      className="flex h-6 items-center gap-1 rounded-full bg-[#6c757d] px-2.5 text-[12.5px] text-white"
                    >
                      {g.template === "text" ? id : (g.values.find((v) => v.id === id)?.name ?? id)}
                      <X size={12} />
                    </button>
                  )),
                )}
              </div>
            )}
            {!applied && <div className="py-16 text-center text-[#6c757d]">Выберите фильтры и нажмите «Показать»</div>}
            {q.isLoading && <div className="py-16 text-center text-[#6c757d]">Загружаем…</div>}
            {q.isError && <div className="py-16 text-center text-[#e53935]">{(q.error as Error).message}</div>}
            {q.data && cards.length === 0 && <div className="py-16 text-center text-[#6c757d]">Ничего не найдено</div>}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(130px,1fr))] gap-1.5">
              {cards.map((c) => {
                const on = ids.includes(c.model_id);
                return (
                  <button
                    key={c.model_id}
                    type="button"
                    onClick={() => toggle(c)}
                    title={[c.name, c.price ? `${c.price} ₽` : ""].filter(Boolean).join(" · ")}
                    className="relative overflow-hidden rounded-[8px] bg-[#2b2b2b]"
                    style={on ? { outline: `3px solid ${BLUE}`, outlineOffset: -3 } : undefined}
                  >
                    <img src={c.photo_url} alt={c.name} loading="lazy" draggable={false} className="aspect-[2/3] w-full object-cover" />
                    <span
                      className="absolute right-1.5 top-1.5 grid size-6 place-items-center rounded-full border-2 border-white text-white"
                      style={{ background: on ? BLUE : "rgba(0,0,0,.25)" }}
                    >
                      {on && <Check size={14} />}
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        setLike(c);
                      }}
                      onKeyDown={(e) => e.key === "Enter" && (e.stopPropagation(), setLike(c))}
                      className="absolute bottom-1.5 left-1.5 flex h-6 items-center gap-1 rounded-full bg-black/55 px-2 text-[12px] text-white hover:bg-black/75"
                      title="Похожие на это фото: где лежат такие и похожие платья"
                      data-annot="похожие в каталоге"
                    >
                      <Images size={13} /> Похожие
                    </span>
                  </button>
                );
              })}
            </div>
            {q.isFetchingNextPage && <div className="py-4 text-center text-[#6c757d]">Загружаем ещё…</div>}
          </div>
          <div className="flex flex-none flex-wrap items-center gap-3 border-t border-[#d9d9d9] bg-[#f2f2f7] px-3 py-2.5">
            <span className="text-[#6c757d]">
              {offer.isError ? <span className="text-[#e53935]">{(offer.error as Error).message}</span> : `Отмечено: ${picked.length} из ${MAX_PICK}`}
            </span>
            {picked.length > 0 && (
              <button type="button" onClick={() => setPicked([])} className="text-[#007bff] hover:underline">
                снять
              </button>
            )}
            <button
              type="button"
              disabled={picked.length === 0 || offer.isPending}
              onClick={() => offer.mutate()}
              className="order-first h-9 rounded-[6px] px-4 font-medium text-white disabled:opacity-50"
              style={{ background: BLUE }}
              title="Фото отмеченных платьев попадут в поле ответа — отправите сами"
              data-annot="фото в ответ"
            >
              {offer.isPending ? "Готовим…" : `Фото в ответ${picked.length ? ` (${picked.length})` : ""}`}
            </button>
          </div>
          {like && (
            <div className="absolute inset-0 z-10 flex flex-col bg-white" data-annot="похожие на фото каталога">
              <div className="flex h-10 flex-none items-center gap-2 border-b border-[#d9d9d9] bg-[#f2f2f7] px-3 text-[16px]">
                <button type="button" onClick={() => setLike(null)} className="flex items-center gap-1 text-[#007bff]">
                  <ArrowLeft size={16} /> К выдаче
                </button>
                <span className="min-w-0 flex-1 truncate text-center">Похожие на {like.name}</span>
                <button type="button" onClick={onClose} title="Закрыть" className="rounded p-1 text-[#6c757d] hover:bg-black/5">
                  <X size={18} />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-3">
                <div className="flex gap-3 pt-3">
                  <img src={like.photo_url} alt={like.name} className="aspect-[2/3] w-24 flex-none rounded-[8px] object-cover" />
                  <div className="text-[13px] text-[#6c757d]">
                    <div className="font-medium text-[#212529]">{like.name}</div>
                    {like.price ? <div>{like.price} ₽</div> : null}
                  </div>
                </div>
                <SerialModels
                  key={`${like.model_id}:${city}`}
                  of={{ model: like.model_id }}
                  start={city}
                  title="Похожие"
                  onOffer={(text, files) => {
                    onOffer(text, files);
                    onClose();
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** «Все» у группы: полный список значений с поиском, поверх фильтров — как боковая панель Резерва. */
function AllValues({
  group,
  chosen,
  onFlip,
  onBack,
}: {
  group: CatalogGroup;
  chosen: string[];
  onFlip: (id: string) => void;
  onBack: () => void;
}) {
  const [find, setFind] = useState("");
  const list = group.values.filter((v) => v.full.toLowerCase().includes(find.trim().toLowerCase()));
  return (
    <div className="absolute inset-0 z-10 flex flex-col bg-white">
      <div className="flex h-10 flex-none items-center border-b border-[#d9d9d9] bg-[#f2f2f7] px-3 text-[16px]">
        <button type="button" onClick={onBack} className="text-[#007bff]">
          Назад
        </button>
        <span className="flex-1 pr-12 text-center">{group.name}</span>
      </div>
      <label className="flex flex-none items-center gap-2 border-b border-[#d9d9d9] px-3 py-2 text-[#6c757d]">
        <Search size={16} />
        <input autoFocus value={find} onChange={(e) => setFind(e.target.value)} placeholder="Поиск" className="min-w-0 flex-1 text-[#212529] outline-none" />
      </label>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {list.map((v) => (
          <label key={v.id} className="flex cursor-pointer items-center gap-2.5 border-b border-[#ececec] px-3 py-2 hover:bg-[#f7f7f9]">
            {v.color && <span className="size-4 flex-none rounded-full border border-black/20" style={{ background: v.color }} />}
            <span className="min-w-0 flex-1">{v.full}</span>
            <input
              type={group.multiple ? "checkbox" : "radio"}
              checked={chosen.includes(v.id)}
              onChange={() => {
                onFlip(v.id);
                if (!group.multiple) onBack();
              }}
              onClick={() => !group.multiple && chosen.includes(v.id) && (onFlip(v.id), onBack())}
            />
          </label>
        ))}
      </div>
    </div>
  );
}
